import { inject, Injectable } from '@angular/core';
import {
	ActivatedRouteSnapshot,
	CanActivate,
	CanDeactivate,
	Router,
	RouterStateSnapshot,
	UrlTree,
} from '@angular/router';
import { CmAuthService, SSO_FLOW } from '@ats/cm/core';
import { ClientService } from '@cm/core/client';
import { CultureService } from '@cm/core/culture';
import { StorageService } from '@cm/core/storage';
import { combineLatest, Observable } from 'rxjs';
import { map, take, tap } from 'rxjs/operators';

const URL_SEGMENT_EXCLUSIONS = ['public', 'session-expired'];

@Injectable()
export class CmIntentGuard implements CanActivate, CanDeactivate<any> {
	private readonly store = inject(StorageService);
	private readonly clientService = inject(ClientService);
	private readonly cultureService = inject(CultureService);
	private readonly router = inject(Router);
	private readonly auth = inject(CmAuthService);
	private readonly _redirectKey = SSO_FLOW.REDIRECT_TO;

	constructor() {}

	canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<boolean> {
		const isAuthenticated$ = this.auth.token$.pipe(
			take(1),
			map(token => !!token)
		);

		const culture$ = this.cultureService.culture$.pipe(take(1));
		const client$ = this.clientService.client$.pipe(take(1));
		return combineLatest([isAuthenticated$, culture$, client$]).pipe(
			tap(([isAuthenticated, culture, client]) => {
				const formattedUrl = state.url.replace(`/${client}/${culture.code}`, '');

				if (
					!isAuthenticated &&
					!formattedUrl.startsWith('/login') &&
					!this.isSSO(formattedUrl) &&
					!URL_SEGMENT_EXCLUSIONS.some(segment => formattedUrl.includes(segment))
				) {
					this.store.save(this._redirectKey, formattedUrl, 20);
				}
			}),
			map(() => true)
		);
	}

	canDeactivate(
		component: any,
		currentRoute: ActivatedRouteSnapshot,
		currentState: RouterStateSnapshot,
		nextState?: RouterStateSnapshot
	): Observable<boolean | UrlTree> {
		const redirectUrl = this.store.get(this._redirectKey);

		const culture$ = this.cultureService.culture$.pipe(take(1));
		const client$ = this.clientService.client$.pipe(take(1));
		return combineLatest([culture$, client$]).pipe(
			take(1),
			map(([culture, client]) => {
				const formattedRedirectUrl = `/${client}/${culture.code}${redirectUrl}`;
				const formattedUrl = nextState.url.replace(`/${client}/${culture.code}`, '');

				if (
					redirectUrl &&
					!(nextState.url.includes('login') && !formattedUrl.startsWith('login/sso')) &&
					!this.isSSO(formattedUrl) &&
					decodeURIComponent(nextState.url) !== formattedRedirectUrl
				) {
					const hasQueryParams = redirectUrl.includes('?');
					const redirectUrlWithoutParams = hasQueryParams
						? redirectUrl.slice(0, redirectUrl.indexOf('?'))
						: redirectUrl;
					const queryParams = {};
					if (hasQueryParams) {
						redirectUrl
							.slice(redirectUrl.indexOf('?') + 1)
							.split('&')
							.forEach(queryParamAndValue => {
								const [param, value] = queryParamAndValue.split('=');
								queryParams[param] = value;
							});
					}

					const urlTree = this.router.createUrlTree(
						[
							client,
							culture.code,
							...redirectUrlWithoutParams.split('/').filter(segment => !!segment),
						],
						{
							queryParams,
						}
					);

					return urlTree;
				} else {
					this.store.delete(this._redirectKey);
					return true;
				}
			})
		);
	}

	private isSSO(url: string): boolean {
		const isSSO = url.startsWith('/sso') && !url.startsWith('/sso/authorize');
		return isSSO;
	}
}
